/*
 * Admin panel role scoping.
 *
 * This is the UI MIRROR of the real access control, which is enforced on the
 * backend by middlewares/auth/secure.js. There, allowedRoles is honoured
 * literally and the default is DENY: a route declared secure(["admin"])
 * rejects an "hr" token with 403. HR only reaches endpoints explicitly
 * declared secure(["admin", "hr"]).
 *
 * So this file exists to keep HR from being shown doors that would 403 — it is
 * not the thing standing between HR and a salary payout. If you widen HR access
 * here, you MUST widen it on the matching backend route too, or the page will
 * render and then fail on every request.
 *
 * Endpoints granted to HR today (keep in sync):
 *   user/user-list, user/user-profile, user/form-data
 *   listener/listener-list, listeners-list, listener-profile/:id,
 *   listener-applications, listener-profiles, listener-request-approval,
 *   reject-request, send-onboarding-form-1, send-onboarding-form-2,
 *   listener-profile-form-link, listener-profile-update
 *   admin/monthly-insights
 *   admin/get-session-rejections      (Call Rejections table)
 *   monitoring/online-now             (Online Now list)
 *   monitoring/rejection-focus        (rejection scorecard + focus strip)
 *   monitoring/force-offline, /wake   (per-row ghost actions, one listener at a
 *                                      time; force-offline refuses if that
 *                                      listener actually has a live socket)
 * Deliberately NOT granted: refunds, recharges, payouts/pay-salary, wallet,
 * GST, coupons, plans, admin management, and monitoring/sweep-ghosts — one
 * confirmed sweep can offline and push up to 200 listeners, which is a
 * different class of action from clicking a single row. The Monitoring page
 * hides the sweep bar for HR, but the backend route is what enforces it.
 */

export const ROLE_ADMIN = 'admin';
export const ROLE_HR = 'hr';
export const ROLE_FINANCE = 'finance';
export const ROLE_COUNSELLOR_HEAD = 'counsellor_head';

/** Read the role captured at login (Login.jsx stores it in localStorage). */
export function getRole() {
  try {
    return (localStorage.getItem('role') || ROLE_ADMIN).toLowerCase();
  } catch (_) {
    return ROLE_ADMIN;
  }
}

export function isHR() {
  return getRole() === ROLE_HR;
}

export function isFinance() {
  return getRole() === ROLE_FINANCE;
}

/*
 * Finance sees the payout pages only (prepare/approve cycles, bank files, payout profiles). The backend
 * enforces this: routes/admin/payout/payout.js is secure(["admin","finance"]) and every other route
 * denies "finance" by default. This list only keeps finance from being shown doors that would 403.
 */
export const FINANCE_ALLOWED_PREFIXES = ['/dashboard/payout'];

export function isCounsellorHead() {
  return getRole() === ROLE_COUNSELLOR_HEAD;
}

/*
 * The Head of Counsellors (login role "counsellor_head") sees the Counsellors vertical only, minus the Config
 * page (the config / head / add-counsellor endpoints are admin/hr on the backend, which enforces it too).
 */
export const COUNSELLOR_HEAD_ALLOWED_PREFIXES = ['/dashboard/counsellors'];
export const COUNSELLOR_HEAD_DENIED_PREFIXES = ['/dashboard/counsellors/config'];

/** Where a role lands after login, and where it is sent when bounced from a page it cannot open. */
export function defaultLandingPath(role = getRole()) {
  if (role === ROLE_COUNSELLOR_HEAD) return '/dashboard/counsellors/applications';
  if (role === ROLE_FINANCE) return '/dashboard/payout';
  if (role === ROLE_HR) return '/dashboard/listener-management/listeners-list';
  return '/dashboard/analytics';
}

/*
 * Route prefixes an HR user may open. Anything not matched here is hidden in
 * the sidebar and blocked by RoleRoute.
 *
 * Per the HR brief:
 *   - complete listener management (listeners, application requests,
 *     profile approvals) — but NOT money refunds
 *   - business insights: payroll (listener online hours, daily call time,
 *     penalty) and listener business
 *   - user list — restricted to candidates who have been sent a form
 */
export const HR_ALLOWED_PREFIXES = [
  '/dashboard/user-management/users-list',
  // Candidate detail — reachable from the "View" action on the user list, and
  // backed by user/user-profile which HR is granted. Without this the View
  // button would bounce them straight back.
  '/dashboard/user-management/profile-view',
  '/dashboard/listener-management',
  '/dashboard/business-insights',
  // Status & stories — HR moderates listener status posts.
  '/dashboard/status',
  '/dashboard/community',
  // Monitoring — HR gets the Online Now tab only (who is actually reachable
  // right now). The page itself narrows the tab list for HR; the other tabs
  // call admin-only endpoints and would 403.
  '/dashboard/monitoring',
  // Call Rejections — the raw table plus the "where to focus" summary, so HR
  // can see who is declining calls and why.
  '/dashboard/rejections',
];

/**
 * Paths an HR user must never reach, even when nested under an allowed
 * prefix. Checked before the allow list.
 */
export const HR_DENIED_PREFIXES = [
  '/dashboard/payment-management',
  '/dashboard/recharge-charges',
];

export function canAccessPath(pathname, role = getRole()) {
  if (role === ROLE_COUNSELLOR_HEAD) {
    if (COUNSELLOR_HEAD_DENIED_PREFIXES.some((p) => pathname.startsWith(p))) return false;
    return COUNSELLOR_HEAD_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  if (role === ROLE_FINANCE) return FINANCE_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  if (role !== ROLE_HR) return true;
  if (HR_DENIED_PREFIXES.some((p) => pathname.startsWith(p))) return false;
  return HR_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Filter the sidebar groups for the active role. Drops child items the role
 * cannot reach, then drops any parent left with no children.
 */
export function filterNavGroups(groups, role = getRole()) {
  if (role !== ROLE_HR && role !== ROLE_FINANCE && role !== ROLE_COUNSELLOR_HEAD) return groups;

  return groups
    .map((group) => {
      const items = group.items
        .map((item) => {
          if (item.children) {
            const children = item.children.filter((c) => canAccessPath(c.path, role));
            return children.length ? { ...item, children } : null;
          }
          return item.path && canAccessPath(item.path, role) ? item : null;
        })
        .filter(Boolean);
      return items.length ? { ...group, items } : null;
    })
    .filter(Boolean);
}
